/**
 * AgentWallets, Admin Dashboard Page for Genesis Vanity Wallets
 *
 * Renders a paginated, filterable table of pre-created vanity wallets from crypto-api.
 * Uses the exact same UI components as AgenticPaymentAirdrop for consistent, modern styling.
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { getRequest, postRequest } from "../../utils/httpRequest";
import { pageHeaderStyles, commonStyles } from "./styles/styles";
import {
    Pagination,
    StatsCard,
    StatsContainer,
    LoadingSpinner,
    FilterBar,
    SearchInput,
    ResultsBadge,
} from "./components";
import "./styles/styles.scss";
import "./styles.scss";

const WALLETS_PER_PAGE = 50;

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

// Only provisioned wallets already owned by (or mid-transfer to) a user can be
// re-sent. Reserved / upcoming / available (unprovisioned) are rejected by the BE,
// so we don't offer the action on them.
const TRANSFERABLE_STATUSES = ["claimed", "completed", "pending_transfer"];
const canTransferWallet = (w) =>
    TRANSFERABLE_STATUSES.includes(w.status) && !!w.hederaAccountId;

// A wallet is "minted" (provisioned on-chain) once it has a Hedera account id.
// Upcoming catalog names (mint-after-pay) have none until the buyer pays.
const isMinted = (w) => !!(w.hederaAccountId || w.evmAddress || w.hederaEvmAddress);

// The admin Wallet Statement for this agent: its token movements, read from the
// mirror node, with the owner's email when the wallet is claimed.
const walletStatementUrl = (w) => {
    const params = new URLSearchParams({ account: w.hederaAccountId, name: w.vanityName || "" });
    if (w.user?.email) params.set("email", w.user.email);
    return `/admin/wallet_statement?${params.toString()}`;
};

const miniBadge = (bg, color, border) => ({
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: "10px",
    fontSize: "11px",
    fontWeight: 700,
    background: bg,
    color,
    border: `1px solid ${border}`,
    whiteSpace: "nowrap",
});

// Translate the UI filters into query params for the genesis_wallets proxy.
// A UUID query is an exact user lookup; anything else is a vanity-name substring.
// Server-side filtering is required, the table only holds one page of 50k rows.
const applyWalletFilters = (params, { status, wave, type, query } = {}) => {
    if (status && status !== "all") params.set("status", status);
    if (wave && wave !== "all") params.set("wave", wave);
    if (type === "premium") params.set("isPremium", "true");
    if (type === "standard") params.set("isPremium", "false");

    const q = (query || "").trim();
    if (q) {
        if (UUID_RE.test(q)) params.set("userId", q);
        else params.set("name", q);
    }
};

const AgentWallets = ({
    hederaNetwork = "testnet",
    hederaChainId = 296,
}) => {
    // State management
    const [wallets, setWallets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [error, setError] = useState(null);

    // Filter & Search states
    const [statusFilter, setStatusFilter] = useState("all");
    const [waveFilter, setWaveFilter] = useState("all");
    const [typeFilter, setTypeFilter] = useState("all");
    // ?search= prefills the search, so other admin pages (the User Statement)
    // can link straight to one agent.
    const initialSearch = typeof window === "undefined" ? "" : new URLSearchParams(window.location.search).get("search") || "";
    const [searchQuery, setSearchQuery] = useState(initialSearch);
    const [activeSearch, setActiveSearch] = useState(initialSearch);

    // Tooltip/Clipboard state
    const [copiedId, setCopiedId] = useState(null);

    // Export state
    const [exporting, setExporting] = useState(false);

    // Transfer-to-user state
    const [transferWallet, setTransferWallet] = useState(null);
    const [recipientEmail, setRecipientEmail] = useState("");
    const [transferring, setTransferring] = useState(false);
    const [transferError, setTransferError] = useState(null);
    const [transferSuccess, setTransferSuccess] = useState(null);

    // Fetch ALL wallets for export (paginate through everything)
    const fetchAllWallets = async () => {
        const batchSize = 5000;
        let allRows = [];
        let offset = 0;
        let total = Infinity;

        while (allRows.length < total) {
            const params = new URLSearchParams({
                limit: batchSize.toString(),
                offset: offset.toString(),
            });
            applyWalletFilters(params, { status: statusFilter, wave: waveFilter, type: typeFilter, query: activeSearch });

            const response = await getRequest(`/admin/agent_wallets/genesis_wallets?${params.toString()}`);
            if (!response.success || !response.data) break;

            const batch = response.data.rows || [];
            allRows = allRows.concat(batch);
            total = response.data.count || allRows.length;
            if (batch.length === 0) break;
            offset += batchSize;
        }
        return allRows;
    };

    // Build CSV string from wallet rows
    const buildCsv = (rows) => {
        const headers = [
            "Vanity Name", "Hedera Account", "Minted", "Paid", "Type", "Wave", "Status",
            "Bounty (USDC)", "Bounty Tier",
            "User Name", "User Email", "User ID",
            "Claimed At", "Reserved At", "Payout Method"
        ];

        const escapeField = (val) => {
            if (val == null) return "";
            const str = String(val);
            if (str.includes(",") || str.includes('"') || str.includes("\n")) {
                return `"${str.replace(/"/g, '""')}"`;
            }
            return str;
        };

        const csvRows = [headers.join(",")];
        rows.forEach((w) => {
            const user = w.user || {};
            const userName = user.full_name ||
                [user.first_name, user.last_name].filter(Boolean).join(" ") ||
                user.username || "";
            csvRows.push([
                w.vanityName,
                w.hederaAccountId,
                isMinted(w) ? "Minted" : "Not minted",
                w.paid === true ? "Yes" : w.paid === false ? "No" : "",
                w.isPremium ? "Premium" : "Standard",
                w.wave,
                w.status,
                w.bountyAmountUsdc,
                w.bountyTier,
                userName,
                user.email,
                w.claimedByUserId,
                w.claimedAt,
                w.reservedAt,
                w.payoutMethod,
            ].map(escapeField).join(","));
        });
        return csvRows.join("\n");
    };

    // Trigger file download in browser
    const downloadFile = (content, filename, mimeType) => {
        const blob = new Blob([content], { type: mimeType });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    // Export handler
    const handleExport = async (format) => {
        setExporting(true);
        try {
            const allRows = await fetchAllWallets();
            const today = new Date().toISOString().slice(0, 10);

            if (format === "json") {
                downloadFile(JSON.stringify(allRows, null, 2), `agent_wallets_${today}.json`, "application/json");
            } else {
                downloadFile(buildCsv(allRows), `agent_wallets_${today}.csv`, "text/csv");
            }
        } catch (err) {
            console.error("Export error:", err);
            setError("Failed to export wallets. Please try again.");
        } finally {
            setExporting(false);
        }
    };

    // Debounce the free-text search so we don't hit the 50k endpoint per keystroke.
    useEffect(() => {
        const handle = setTimeout(() => setActiveSearch(searchQuery.trim()), 400);
        return () => clearTimeout(handle);
    }, [searchQuery]);

    // Reset page to 1 when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [statusFilter, waveFilter, typeFilter, activeSearch]);

    // Fetch wallets from our local ActiveAdmin proxy route
    const fetchWallets = useCallback(async (page = 1, status = "all", wave = "all", query = "", type = "all") => {
        setLoading(true);
        setError(null);
        try {
            const offset = (page - 1) * WALLETS_PER_PAGE;
            const params = new URLSearchParams({
                limit: WALLETS_PER_PAGE.toString(),
                offset: offset.toString(),
            });

            applyWalletFilters(params, { status, wave, type, query });

            const response = await getRequest(`/admin/agent_wallets/genesis_wallets?${params.toString()}`);

            if (response.success && response.data) {
                setWallets(response.data.rows || []);
                setTotalCount(response.data.count || 0);
            } else {
                setWallets([]);
                setTotalCount(0);
                if (response.error) setError(response.error);
            }
        } catch (err) {
            console.error("Error fetching agent wallets:", err);
            setWallets([]);
            setTotalCount(0);

            const apiError =
                err?.response?.data?.error ||
                err?.response?.data?.message ||
                err?.message;

            setError(apiError || "Failed to fetch genesis wallets. Please try again.");
        } finally {
            setLoading(false);
        }
    }, []);

    // Fetch on state change
    useEffect(() => {
        fetchWallets(currentPage, statusFilter, waveFilter, activeSearch, typeFilter);
    }, [currentPage, statusFilter, waveFilter, typeFilter, activeSearch, fetchWallets]);

    // Handle search input change (without immediate submit if desired, but we map it directly to SearchInput)
    // Actually, Airdrop SearchInput triggers onChange. We'll set activeSearch on change for simplicity, 
    // or use SearchQuery and set activeSearch manually. We'll just bind directly to activeSearch for instant search.

    // ── Transfer-to-user handlers ──
    const openTransfer = (wallet) => {
        setTransferWallet(wallet);
        setRecipientEmail(wallet.status === "pending_transfer" ? (wallet.transferredToEmail || "") : "");
        setTransferError(null);
        setTransferSuccess(null);
    };

    const closeTransfer = () => {
        if (transferring) return;
        setTransferWallet(null);
        setRecipientEmail("");
        setTransferError(null);
    };

    const submitTransfer = async () => {
        const email = recipientEmail.trim();
        if (!EMAIL_RE.test(email)) {
            setTransferError("Enter a valid recipient email.");
            return;
        }
        setTransferring(true);
        setTransferError(null);
        try {
            const res = await postRequest("/admin/agent_wallets/transfer", {
                walletId: String(transferWallet.id),
                recipientEmail: email,
            });
            if (res && res.success) {
                const data = res.data || {};
                setTransferSuccess(data.message || `Sent to ${data.transferredToEmail || email}, pending claim.`);
                setTransferWallet(null);
                setRecipientEmail("");
                fetchWallets(currentPage, statusFilter, waveFilter, activeSearch, typeFilter);
            } else {
                setTransferError((res && res.error) || "Transfer failed.");
            }
        } catch (err) {
            setTransferError(err?.data?.error || err?.data?.message || "Transfer failed. Please try again.");
        } finally {
            setTransferring(false);
        }
    };

    // Copy to clipboard helper
    const handleCopyToClipboard = (text, id) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopiedId(id);
            setTimeout(() => setCopiedId(null), 2000);
        });
    };

    // Calculate total pages
    const totalPages = Math.ceil(totalCount / WALLETS_PER_PAGE) || 1;

    // Calculate serial number (continuous across pages)
    const getSerialNumber = (index) => {
        return (currentPage - 1) * WALLETS_PER_PAGE + index + 1;
    };

    // Format currency helper
    const formatCurrency = (amount) => {
        const parsed = parseFloat(amount);
        if (isNaN(parsed)) return "$0.00";
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
        }).format(parsed);
    };

    // Build a Set of payout transaction hashes that appear more than once
    const duplicateHashes = useMemo(() => {
        const counts = {};
        wallets.forEach((w) => {
            const hash = w.payoutTransactionHash || w.payout_transaction_hash;
            if (hash) {
                counts[hash] = (counts[hash] || 0) + 1;
            }
        });
        return new Set(Object.keys(counts).filter((h) => counts[h] > 1));
    }, [wallets]);

    return (
        <div className="liquidity-pool-airdrop" style={{ padding: "0 0 20px 0" }}>
            {/* ── Page Header ── */}
            <div style={{ ...pageHeaderStyles.container, marginBottom: "20px" }}>
                <h1 style={pageHeaderStyles.title}>
                    Genesis Agent Wallets
                </h1>
                <div style={pageHeaderStyles.infoBox}>
                    <span style={pageHeaderStyles.infoLabel}>Network:</span>
                    <code style={pageHeaderStyles.infoValue}>{hederaNetwork.charAt(0).toUpperCase() + hederaNetwork.slice(1)}</code>
                </div>
            </div>

            <div className="tab-content" style={{ borderTopLeftRadius: "6px", borderTopRightRadius: "6px" }}>
                {/* ── Error Banner ── */}
                {error && (
                    <div className="flash flash_error" style={{ marginBottom: "16px", padding: "12px 16px" }}>
                        <span>{error}</span>
                        <button
                            className="flash-close"
                            onClick={() => setError(null)}
                        >
                            ×
                        </button>
                    </div>
                )}

                {/* ── Transfer success banner ── */}
                {transferSuccess && (
                    <div className="flash flash_notice" style={{ marginBottom: "16px", padding: "12px 16px" }}>
                        <span>{transferSuccess}</span>
                        <button className="flash-close" onClick={() => setTransferSuccess(null)}>×</button>
                    </div>
                )}

                {/* ── Transfer-to-user modal ── */}
                {transferWallet && (
                    <div
                        onClick={closeTransfer}
                        style={{
                            position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
                            display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
                        }}
                    >
                        <div
                            onClick={(e) => e.stopPropagation()}
                            style={{
                                background: "#fff", borderRadius: "10px", width: "440px", maxWidth: "92vw",
                                boxShadow: "0 20px 48px rgba(0,0,0,0.25)", overflow: "hidden",
                            }}
                        >
                            <div style={{ background: "#2e3b61", color: "#fff", padding: "16px 20px", fontWeight: 700 }}>
                                Transfer wallet to a user
                            </div>
                            <div style={{ padding: "20px" }}>
                                <div style={{ marginBottom: "14px", fontSize: "14px", color: "#333" }}>
                                    <div style={{ fontWeight: 700, color: "#2e3b61", fontSize: "16px" }}>
                                        {transferWallet.vanityName}
                                    </div>
                                    <code style={{ ...commonStyles.code, fontSize: "12px" }}>
                                        {transferWallet.hederaAccountId}
                                    </code>
                                    <div style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>
                                        Status: {transferWallet.status}
                                    </div>
                                </div>

                                <label style={{ display: "block", fontSize: "13px", fontWeight: 600, color: "#555", marginBottom: "6px" }}>
                                    Recipient email
                                </label>
                                <input
                                    type="email"
                                    value={recipientEmail}
                                    onChange={(e) => setRecipientEmail(e.target.value)}
                                    placeholder="user@email.com"
                                    disabled={transferring}
                                    style={{
                                        width: "100%", padding: "10px 12px", border: "1px solid #ccc",
                                        borderRadius: "6px", fontSize: "14px", boxSizing: "border-box",
                                    }}
                                />

                                <div style={{ background: "#f4f6fb", borderRadius: "6px", padding: "10px 12px", fontSize: "12px", color: "#666", margin: "12px 0 16px" }}>
                                    The recipient is emailed and claims the wallet from their My Agents. This
                                    sets the wallet to <strong>pending_transfer</strong>; you can re-send to fix a
                                    typo'd email.
                                </div>

                                {transferError && (
                                    <div style={{ background: "#f8d7da", color: "#721c24", border: "1px solid #f5c6cb", borderRadius: "6px", padding: "10px 12px", fontSize: "13px", marginBottom: "12px" }}>
                                        {transferError}
                                    </div>
                                )}

                                <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                                    <button
                                        type="button"
                                        onClick={closeTransfer}
                                        disabled={transferring}
                                        style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", minHeight: "38px", boxSizing: "border-box", background: "#e2e3e5", color: "#333", border: "none", borderRadius: "6px", padding: "9px 16px", fontSize: "13px", fontWeight: 600, cursor: "pointer", lineHeight: 1.2 }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={submitTransfer}
                                        disabled={transferring}
                                        style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", minHeight: "38px", boxSizing: "border-box", background: "#2e3b61", color: "#fff", border: "none", borderRadius: "6px", padding: "9px 16px", fontSize: "13px", fontWeight: 600, cursor: transferring ? "not-allowed" : "pointer", opacity: transferring ? 0.6 : 1, lineHeight: 1.2 }}
                                    >
                                        {transferring ? "Sending…" : "Send transfer"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* ── Stats Summary ── */}
                <StatsContainer>
                    <StatsCard
                        value={totalCount.toLocaleString()}
                        label="Total Wallets"
                        variant="primary"
                    />
                    <StatsCard
                        value={waveFilter === "all" ? "All Waves" : `Wave ${waveFilter}`}
                        label="Current Wave"
                        variant="accent"
                    />
                    <StatsCard 
                        value={statusFilter === "all" ? "All" : statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)} 
                        label="Status Filter" 
                        variant="warning" 
                    />
                    <StatsCard 
                        value={loading ? "..." : `${currentPage} / ${totalPages}`} 
                        label="Page" 
                        variant="success" 
                    />
                </StatsContainer>

                {/* ── Filter Bar ── */}
                <FilterBar>
                    <div style={{ display: "flex", alignItems: "center", width: "100%", justifyContent: "space-between", flexWrap: "wrap", gap: "12px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
                            <div style={{ minWidth: "240px" }}>
                                <SearchInput
                                    value={searchQuery}
                                    onChange={setSearchQuery}
                                    placeholder="Search vanity name (or paste user ID)…"
                                />
                            </div>

                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <label style={{ fontSize: "13px", color: "#666", fontWeight: 500 }}>Wave</label>
                                <select
                                    value={waveFilter}
                                    onChange={(e) => setWaveFilter(e.target.value)}
                                    style={{ padding: "8px 12px", border: "1px solid #ccc", borderRadius: "4px", fontSize: "13px" }}
                                >
                                    <option value="all">All Waves</option>
                                    <option value="1">Wave 1</option>
                                    <option value="2">Wave 2</option>
                                    <option value="3">Wave 3</option>
                                    <option value="4">Wave 4</option>
                                    <option value="5">Wave 5</option>
                                </select>
                            </div>

                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <label style={{ fontSize: "13px", color: "#666", fontWeight: 500 }}>Status</label>
                                <select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    style={{ padding: "8px 12px", border: "1px solid #ccc", borderRadius: "4px", fontSize: "13px" }}
                                >
                                    <option value="all">All Statuses</option>
                                    <option value="available">Available</option>
                                    <option value="reserved">Reserved</option>
                                    <option value="claimed">Claimed</option>
                                    <option value="upcoming">Upcoming</option>
                                    <option value="expired">Expired</option>
                                </select>
                            </div>

                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <label style={{ fontSize: "13px", color: "#666", fontWeight: 500 }}>Type</label>
                                <select
                                    value={typeFilter}
                                    onChange={(e) => setTypeFilter(e.target.value)}
                                    style={{ padding: "8px 12px", border: "1px solid #ccc", borderRadius: "4px", fontSize: "13px" }}
                                >
                                    <option value="all">All Types</option>
                                    <option value="premium">Premium</option>
                                    <option value="standard">Standard / Raffle</option>
                                </select>
                            </div>
                        </div>

                        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                            <ResultsBadge
                                showing={totalCount === 0 ? 0 : `${((currentPage - 1) * WALLETS_PER_PAGE) + 1}-${Math.min(currentPage * WALLETS_PER_PAGE, totalCount)}`}
                                total={totalCount}
                            />
                        </div>
                    </div>
                </FilterBar>

                {/* ── Content ── */}
                {loading ? (
                    <LoadingSpinner message="Loading genesis wallets..." />
                ) : wallets.length === 0 ? (
                    <div className="blank_slate" style={{ padding: "60px 20px", textAlign: "center", background: "#f8f9fa", border: "1px solid #e0e0e0", borderRadius: "4px" }}>
                        <p style={{ margin: "0", color: "#666", fontSize: "14px" }}>
                            {activeSearch || statusFilter !== "all" || waveFilter !== "all" || typeFilter !== "all"
                                ? "No wallets match your filters."
                                : "No genesis vanity wallets found."}
                        </p>
                    </div>
                ) : (
                    <>
                        <div style={{ ...commonStyles.panel, marginBottom: 0 }}>
                            <h3 style={commonStyles.panelHeader}>Genesis Vanity Wallets</h3>
                            <div className="index_table_container">
                                <table className="index_table">
                                    <thead>
                                        <tr>
                                            <th style={{ ...commonStyles.tableHeaderCell, color: "#333", width: "50px", textAlign: "center" }}>#</th>
                                            <th style={{ ...commonStyles.tableHeaderCell, color: "#333" }}>Vanity Name</th>
                                            <th style={{ ...commonStyles.tableHeaderCell, color: "#333" }}>Hedera Account</th>
                                            <th style={{ ...commonStyles.tableHeaderCell, color: "#333" }}>Wave</th>
                                            <th style={{ ...commonStyles.tableHeaderCell, color: "#333" }}>Bounty</th>
                                            <th style={{ ...commonStyles.tableHeaderCell, color: "#333" }}>Status</th>
                                            <th style={{ ...commonStyles.tableHeaderCell, color: "#333" }}>Bound User</th>
                                            <th style={{ ...commonStyles.tableHeaderCell, color: "#333" }}>Timestamps</th>
                                            <th style={{ ...commonStyles.tableHeaderCell, color: "#333" }}>Payout Tx Hash</th>
                                            <th style={{ ...commonStyles.tableHeaderCell, color: "#333" }}>Sender Address</th>
                                            <th style={{ ...commonStyles.tableHeaderCell, color: "#333" }}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {wallets.map((wallet, index) => {
                                            const user = wallet.user;
                                            const hasUser = !!user;
                                            const sn = getSerialNumber(index);

                                            return (
                                                <tr key={wallet.id} className={index % 2 === 0 ? "even" : "odd"}>
                                                    <td style={{ ...commonStyles.tableCell, textAlign: "center", color: "#999", fontWeight: 600 }}>
                                                        {sn}
                                                    </td>
                                                    <td style={commonStyles.tableCell}>
                                                        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                                                            <span style={{ fontWeight: 700, fontSize: "13.5px", color: "#2e3b61" }}>
                                                                {wallet.vanityName}
                                                            </span>
                                                            <span style={{ 
                                                                alignSelf: "flex-start", fontSize: "10px", fontWeight: 700, 
                                                                textTransform: "uppercase", padding: "2px 8px", borderRadius: "3px",
                                                                ...(wallet.isPremium 
                                                                    ? { background: "linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)", color: "#e65100", border: "1px solid #ffcc80" }
                                                                    : { background: "#f0f0f0", color: "#888", border: "1px solid #ddd" }
                                                                )
                                                            }}>
                                                                {wallet.isPremium ? "★ Premium" : "Standard"}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td style={commonStyles.tableCell}>
                                                        <div style={{ display: "flex", flexDirection: "column", gap: "5px", alignItems: "flex-start" }}>
                                                            {wallet.hederaAccountId ? (
                                                                <div style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                                                                    <a href={walletStatementUrl(wallet)} title="Open this wallet's statement">
                                                                        <code style={commonStyles.code}>{wallet.hederaAccountId}</code>
                                                                    </a>
                                                                    <button
                                                                        type="button"
                                                                        title="Copy account ID"
                                                                        onClick={() => handleCopyToClipboard(wallet.hederaAccountId, `${wallet.id}-addr`)}
                                                                        style={{ background: "none", border: "none", cursor: "pointer", color: "#999" }}
                                                                    >
                                                                        {copiedId === `${wallet.id}-addr` ? (
                                                                            <span style={{ color: "#28a745", fontSize: "11px", fontWeight: 700 }}>✓</span>
                                                                        ) : (
                                                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                                                                                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                                                                            </svg>
                                                                        )}
                                                                    </button>
                                                                </div>
                                                            ) : (
                                                                <span style={{ color: "#aaa", fontSize: "12px" }}>-</span>
                                                            )}
                                                            <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
                                                                {isMinted(wallet) ? (
                                                                    <span style={miniBadge("#d4edda", "#155724", "#c3e6cb")}>● Minted</span>
                                                                ) : (
                                                                    <span style={miniBadge("#e9ecef", "#6c757d", "#dee2e6")}>○ Not minted</span>
                                                                )}
                                                                {typeof wallet.paid === "boolean" && (
                                                                    wallet.paid
                                                                        ? <span style={miniBadge("#d1ecf1", "#0c5460", "#bee5eb")}>Paid</span>
                                                                        : <span style={miniBadge("#f5f5f5", "#999", "#e0e0e0")}>Unpaid</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td style={commonStyles.tableCell}>
                                                        <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: "12px", fontSize: "12px", fontWeight: 600, background: "#e8f4fd", color: "#0c5460", border: "1px solid #b8daff" }}>
                                                            Wave {wallet.wave}
                                                        </span>
                                                    </td>
                                                    <td style={commonStyles.tableCell}>
                                                        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                                                            <span style={{ fontWeight: 700, fontSize: "13px", color: "#28a745" }}>
                                                                {formatCurrency(wallet.bountyAmountUsdc)}
                                                            </span>
                                                            {wallet.bountyTier && wallet.bountyTier !== "none" && (
                                                                <span style={{ alignSelf: "flex-start", fontSize: "10px", fontWeight: 700, textTransform: "uppercase", padding: "2px 8px", borderRadius: "12px", background: "#fff8e1", color: "#f57f17", border: "1px solid #ffe082" }}>
                                                                    {wallet.bountyTier}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td style={commonStyles.tableCell}>
                                                        <span
                                                            className={`status_tag ${
                                                                wallet.status === "claimed" ? "success" :
                                                                wallet.status === "available" ? "active" :
                                                                wallet.status === "reserved" ? "pending" :
                                                                wallet.status === "upcoming" ? "warning" :
                                                                wallet.status === "expired" ? "failed" :
                                                                "disabled"
                                                            }`}
                                                        >
                                                            {wallet.status}
                                                        </span>
                                                    </td>
                                                    <td style={commonStyles.tableCell}>
                                                        {hasUser ? (
                                                            <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                                                                <span style={{ fontWeight: 600, color: "#2e3b61", fontSize: "13px" }}>
                                                                    {user.full_name || `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.username}
                                                                </span>
                                                                <span style={{ fontSize: "12px", color: "#666" }}>{user.email}</span>
                                                                <span style={{ fontSize: "11px", color: "#999", fontFamily: "monospace" }}>@{user.username}</span>
                                                            </div>
                                                        ) : (wallet.claimedByUserId || wallet.transferredToEmail) ? (
                                                            <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                                                                {wallet.claimedByUserId && (
                                                                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                                                        <span style={{ fontSize: "11px", color: "#666", fontFamily: "monospace" }} title={wallet.claimedByUserId}>
                                                                            {`${String(wallet.claimedByUserId).slice(0, 8)}…${String(wallet.claimedByUserId).slice(-6)}`}
                                                                        </span>
                                                                        <button
                                                                            type="button"
                                                                            title="Copy user ID"
                                                                            onClick={() => handleCopyToClipboard(wallet.claimedByUserId, `${wallet.id}-uid`)}
                                                                            style={{ background: "none", border: "none", cursor: "pointer", color: "#999", padding: 0 }}
                                                                        >
                                                                            {copiedId === `${wallet.id}-uid`
                                                                                ? <span style={{ color: "#28a745", fontSize: "11px", fontWeight: 700 }}>✓</span>
                                                                                : <span style={{ fontSize: "12px" }}>⧉</span>}
                                                                        </button>
                                                                    </div>
                                                                )}
                                                                {wallet.transferredToEmail && (
                                                                    <span style={{ fontSize: "11px", color: "#b7791f" }}>→ {wallet.transferredToEmail}</span>
                                                                )}
                                                                <span style={{ fontSize: "10px", color: "#bbb", fontStyle: "italic" }}>user ID only, no profile joined</span>
                                                            </div>
                                                        ) : (
                                                            <span style={{ color: "#bbb", fontSize: "12px", fontStyle: "italic" }}>, Unclaimed, </span>
                                                        )}
                                                    </td>
                                                    <td style={commonStyles.tableCell}>
                                                        <div style={{ display: "flex", flexDirection: "column", gap: "3px", fontSize: "12px", color: "#666" }}>
                                                            {wallet.status === "claimed" && wallet.claimedAt && (
                                                                <span>Claimed: <strong>{new Date(wallet.claimedAt).toLocaleDateString()}</strong></span>
                                                            )}
                                                            {wallet.status === "reserved" && wallet.reservedAt && (
                                                                <>
                                                                    <span>Reserved: <strong>{new Date(wallet.reservedAt).toLocaleDateString()}</strong></span>
                                                                    {wallet.reservationExpiresAt && (
                                                                        <span style={{ color: "#dc3545", fontWeight: 600 }}>
                                                                            Expires: {new Date(wallet.reservationExpiresAt).toLocaleDateString()}
                                                                        </span>
                                                                    )}
                                                                </>
                                                            )}
                                                            {wallet.status === "available" && (
                                                                <span style={{ color: "#17a2b8", fontWeight: 500 }}>Available to claim</span>
                                                            )}
                                                            {wallet.status === "upcoming" && (
                                                                <span style={{ color: "#4c3f84", fontWeight: 500 }}>Locked, scheduled</span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td style={commonStyles.tableCell}>
                                                        {(() => {
                                                            const hash = wallet.payoutTransactionHash || wallet.payout_transaction_hash;
                                                            if (!hash) return <span style={{ color: "#bbb", fontSize: "12px" }}>-</span>;
                                                            const isDuplicate = duplicateHashes.has(hash);
                                                            return (
                                                                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                                                    <code style={{ ...commonStyles.code, fontSize: "11px", wordBreak: "break-all" }}>
                                                                        {hash.length > 16 ? `${hash.slice(0, 8)}…${hash.slice(-8)}` : hash}
                                                                    </code>
                                                                    <button
                                                                        type="button"
                                                                        title="Copy transaction hash"
                                                                        onClick={() => handleCopyToClipboard(hash, `${wallet.id}-txhash`)}
                                                                        style={{ background: "none", border: "none", cursor: "pointer", color: "#999" }}
                                                                    >
                                                                        {copiedId === `${wallet.id}-txhash` ? (
                                                                            <span style={{ color: "#28a745", fontSize: "11px", fontWeight: 700 }}>✓</span>
                                                                        ) : (
                                                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                                                                                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                                                                            </svg>
                                                                        )}
                                                                    </button>
                                                                    {isDuplicate && (
                                                                        <span style={{
                                                                            display: "inline-flex", alignItems: "center", gap: "3px",
                                                                            fontSize: "10px", fontWeight: 700, color: "#dc3545",
                                                                            background: "#ffeef0", border: "1px solid #f5c6cb",
                                                                            padding: "2px 6px", borderRadius: "3px", whiteSpace: "nowrap",
                                                                        }}>
                                                                            ⚠️ Duplicate
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            );
                                                        })()}
                                                    </td>
                                                    <td style={commonStyles.tableCell}>
                                                        {(() => {
                                                            const addr = wallet.senderWalletAddress || wallet.sender_wallet_address;
                                                            if (!addr) return <span style={{ color: "#bbb", fontSize: "12px" }}>-</span>;
                                                            return (
                                                                <div style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                                                                    <code style={{ ...commonStyles.code, fontSize: "11px", wordBreak: "break-all" }}>
                                                                        {addr.length > 16 ? `${addr.slice(0, 8)}…${addr.slice(-8)}` : addr}
                                                                    </code>
                                                                    <button
                                                                        type="button"
                                                                        title="Copy sender address"
                                                                        onClick={() => handleCopyToClipboard(addr, `${wallet.id}-sender`)}
                                                                        style={{ background: "none", border: "none", cursor: "pointer", color: "#999" }}
                                                                    >
                                                                        {copiedId === `${wallet.id}-sender` ? (
                                                                            <span style={{ color: "#28a745", fontSize: "11px", fontWeight: 700 }}>✓</span>
                                                                        ) : (
                                                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                                                                                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                                                                            </svg>
                                                                        )}
                                                                    </button>
                                                                </div>
                                                            );
                                                        })()}
                                                    </td>
                                                    <td style={commonStyles.tableCell}>
                                                        {canTransferWallet(wallet) ? (
                                                            <button
                                                                type="button"
                                                                onClick={() => openTransfer(wallet)}
                                                                style={{
                                                                    display: "inline-flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "center",
                                                                    minHeight: "32px",
                                                                    boxSizing: "border-box",
                                                                    background: "#2e3b61",
                                                                    color: "#fff",
                                                                    border: "none",
                                                                    borderRadius: "4px",
                                                                    padding: "6px 12px",
                                                                    fontSize: "12px",
                                                                    fontWeight: 600,
                                                                    lineHeight: 1.2,
                                                                    cursor: "pointer",
                                                                    whiteSpace: "nowrap",
                                                                }}
                                                            >
                                                                Transfer to user
                                                            </button>
                                                        ) : (
                                                            <span style={{ color: "#bbb", fontSize: "12px" }}>-</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                            totalItems={totalCount}
                            itemsPerPage={WALLETS_PER_PAGE}
                        />

                        {/* Download links, matching ActiveAdmin hedera_wallet format */}
                        <div style={{ padding: "10px 0", fontSize: "13px" }}>
                            <span style={{ color: "#666" }}>Download: </span>
                            <button

                                type="button"

                                disabled={exporting}

                                onClick={() => { if (!exporting) handleExport("csv"); }}

                                style={{

                                    color: "#e5873c",

                                    textDecoration: "none",

                                    cursor: exporting ? "not-allowed" : "pointer",

                                    marginRight: "4px",

                                    background: "none",

                                    border: "none",

                                    padding: 0,

                                    fontSize: "inherit",

                                }}

                                onMouseEnter={(e) => { e.currentTarget.style.textDecoration = "underline"; }}
                                onMouseLeave={(e) => { e.currentTarget.style.textDecoration = "none"; }}
                                onFocus={(e) => { e.currentTarget.style.textDecoration = "underline"; }}

                                onBlur={(e) => { e.currentTarget.style.textDecoration = "none"; }}

                            >
                                {exporting ? "Exporting…" : "CSV"}
                            </button>

                            {" "}
                            <button

                                type="button"

                                disabled={exporting}

                                onClick={() => { if (!exporting) handleExport("json"); }}

                                style={{

                                    color: "#e5873c",

                                    textDecoration: "none",

                                    cursor: exporting ? "not-allowed" : "pointer",

                                    background: "none",

                                    border: "none",

                                    padding: 0,

                                    fontSize: "inherit",

                                }}

                                onMouseEnter={(e) => { e.currentTarget.style.textDecoration = "underline"; }}
                                onMouseLeave={(e) => { e.currentTarget.style.textDecoration = "none"; }}
                                onFocus={(e) => { e.currentTarget.style.textDecoration = "underline"; }}

                                onBlur={(e) => { e.currentTarget.style.textDecoration = "none"; }}

                            >
                                {exporting ? "Exporting…" : "JSON"}

                            </button>

                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default AgentWallets;
